import React from 'react';
import { Card, CardContent, Box, Typography } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconName } from '@fortawesome/fontawesome-svg-core';
import { useParams } from 'react-router-dom';
import { useRouteNavigation } from '../../../hooks/useRouteNavigation';

interface QuickAccessCardProps {
  title: string;
  description?: string;
  icon: IconName;
  href?: string;
  /** Atalhos da home customizada: abre em nova aba mesmo quando o link é interno. */
  openInNewTab?: boolean;
}

export default function QuickAccessCard({ title, description, icon, href = '#', openInNewTab = false }: QuickAccessCardProps) {
  const navigateWithPrefix = useRouteNavigation();
  const params = useParams<{ state?: string; city?: string }>();
  const isDisabled = href === '#';
  const isExternalLink = href?.startsWith('http');
  const opensNewTab = !isDisabled && (isExternalLink || openInNewTab);
  const isInternalRoute = !isDisabled && !isExternalLink && !openInNewTab;

  const getHref = () => {
    if (isDisabled) return undefined;
    if (isExternalLink) return href;

    // Gera o href com prefixo se estamos em uma rota com state/city
    const cleanPath = href.startsWith('/') ? href.substring(1) : href;
    if (params.state && params.city) {
      return `/${params.state}/${params.city}/${cleanPath}`;
    }
    return `/${cleanPath}`;
  };

  const handleClick = (e: React.MouseEvent) => {
    if (isDisabled) {
      e.preventDefault();
      return;
    }

    if (isInternalRoute) {
      e.preventDefault();
      navigateWithPrefix(href);
    }
    // Para links externos, deixa o comportamento padrão do navegador
  };

  return (
    <Card
      component="a"
      href={getHref()}
      target={opensNewTab ? '_blank' : undefined}
      rel={opensNewTab ? 'noopener noreferrer' : undefined}
      onClick={handleClick}
      sx={{
        textDecoration: 'none',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        p: 3,
        cursor: isDisabled ? 'not-allowed' : 'pointer',
        transition: 'all 0.3s ease',
        borderRadius: 2,
        opacity: isDisabled ? 0.5 : 1,
        ...(!isDisabled && {
          '&:hover': {
            transform: 'translateY(-8px)',
            boxShadow: 6,
            '& .icon-wrapper': {
              transform: 'scale(1.1)',
              backgroundColor: 'primary.main',
            },
            '& .MuiSvgIcon-root': {
              color: 'white',
            },
          },
        }),
      }}
    >
      <CardContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2,
          width: '100%',
        }}
      >
        <Box
          className="icon-wrapper"
          sx={{
            width: 80,
            height: 80,
            borderRadius: '50%',
            backgroundColor: 'primary.light',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.3s ease',
          }}
        >
          <FontAwesomeIcon
            icon={icon}
            style={{
              fontSize: '2rem',
              color: 'inherit',
            }}
          />
        </Box>

        <Typography
          variant="h6"
          component="div"
          textAlign="center"
          sx={{
            fontWeight: 600,
            fontSize: '1rem',
            color: 'text.primary',
            lineHeight: 1.3,
          }}
        >
          {title}
        </Typography>

        {description && (
          <Typography
            variant="body2"
            textAlign="center"
            sx={{
              color: 'text.secondary',
              fontSize: '0.85rem',
              lineHeight: 1.4,
            }}
          >
            {description}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}
